import { useCallback, type JSX } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import type { ChatMessage, ChatComponentContent } from '../../chatbot.types';
import { useChatbotConfig, useChatbotEventBridge } from '../../ChatbotProvider';
import { ChatAvatar } from '../chat-avatar';
import { ChatActions } from '../chat-actions';
import { ChatThinkingIndicator } from '../chat-thinking-indicator';
import { ChatSingleSelect } from '../chat-single-select';
import { ChatMultipleSelect } from '../chat-multiple-select';

type Props = {
  message: ChatMessage;
  onSelectOption: (messageId: string, optionId: string) => void;
  onSelectOptions: (messageId: string, optionIds: string[]) => void;
};

const ChatComponentRenderer = ({ messageId, content }: { messageId: string; content: ChatComponentContent }) => {
  const { componentRegistry } = useChatbotConfig();
  const Component = componentRegistry?.[content.componentId];
  if (!Component) return <span className='text-muted'>[{content.componentId}]</span>;
  return <Component messageId={messageId} {...content.props} />;
};

export const ChatBubble = ({ message, onSelectOption, onSelectOptions }: Props): JSX.Element => {
  const { role, content } = message;
  const { roleLabels } = useChatbotConfig();
  const bridge = useChatbotEventBridge();

  const handleRevert = useCallback(() => {
    bridge?.emit('revert');
  }, [bridge]);

  return (
    <div
      className={`chatbot-bubble-row chatbot-bubble-row--${role}${message.removing ? ' chatbot-bubble-row--removing' : ''}`}
    >
      <ChatAvatar role={role} />
      <div className='chatbot-bubble-wrap'>
        {role !== 'user' && roleLabels?.[role] && <div className='chatbot-role-label'>{roleLabels[role]}</div>}
        <div className={`chatbot-bubble chatbot-bubble--${role}`}>
          {content.type === 'text' && (
            <div className='chatbot-markdown'>
              <Markdown remarkPlugins={[remarkBreaks]}>{content.text}</Markdown>
            </div>
          )}

          {content.type === 'markdown' && (
            <div className='chatbot-markdown'>
              <Markdown remarkPlugins={[remarkBreaks]}>{content.markdown}</Markdown>
            </div>
          )}

          {content.type === 'thinking' && <ChatThinkingIndicator messages={content.messages} />}

          {content.type === 'single-select' && (
            <ChatSingleSelect messageId={message.id} content={content} onSelect={onSelectOption} />
          )}

          {content.type === 'multiple-select' && (
            <ChatMultipleSelect messageId={message.id} content={content} onConfirm={onSelectOptions} />
          )}

          {content.type === 'actions' && <ChatActions content={content} />}

          {content.type === 'component' && <ChatComponentRenderer messageId={message.id} content={content} />}
        </div>

        {message.canRevert && (
          <button
            type='button'
            className='chatbot-bubble-revert-btn'
            onClick={handleRevert}
            aria-label='Antwoord ongedaan maken'
            title='Antwoord ongedaan maken'
          >
            <svg
              className='chatbot-revert-icon'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='1 4 1 10 7 10' />
              <path d='M3.51 15a9 9 0 1 0 2.13-9.36L1 10' />
            </svg>
            Wijzig antwoord
          </button>
        )}
      </div>
    </div>
  );
};
